import { AppLayout } from "@/components/layout/AppLayout";
import { useScaleMetrics, AttentionItem, RiskCard, ItemStatus } from "@/hooks/useScaleMetrics";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle,
  Eye,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Users,
  Clock,
  Shield,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import { useState } from "react";

const levelStyles: Record<string, string> = {
  needs_attention: "bg-red-500/10 text-red-600 border-red-500/20",
  monitor: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  stable: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  ignore: "bg-muted text-muted-foreground border-border",
};

const levelLabels: Record<string, string> = {
  needs_attention: "Needs attention",
  monitor: "Monitor",
  stable: "Stable",
  ignore: "No action needed",
};

const statusOptions: { value: ItemStatus; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "handled", label: "Handled", icon: CheckCircle2 },
  { value: "delegated", label: "Delegate", icon: Users },
  { value: "review_later", label: "Review later", icon: Clock },
];

const severityStyles: Record<string, string> = {
  high: "border-l-red-500",
  medium: "border-l-amber-500",
  low: "border-l-emerald-500",
};

const trendIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  worsening: TrendingDown,
  stable: Minus,
  improving: TrendingUp,
};

const AttentionCard = ({ item }: { item: AttentionItem }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<ItemStatus>(item.status);

  if (status === "handled") return null;

  return (
    <Card className={status === "delegated" || status === "review_later" ? "opacity-60" : ""}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-muted shrink-0">
            <Eye className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground leading-tight">{item.title}</h3>
              <Badge variant="outline" className={levelStyles[item.level]}>
                {levelLabels[item.level]}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1 capitalize">{item.reason}</p>

            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
                {expanded ? "Less" : "Details"}
              </Button>
              {statusOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant="ghost"
                  size="sm"
                  className="text-xs h-8 text-muted-foreground"
                  onClick={() => setStatus(opt.value)}
                >
                  <opt.icon className="h-3.5 w-3.5 mr-1" />
                  {opt.label}
                </Button>
              ))}
            </div>

            {expanded && (
              <div className="mt-4 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Ranking factors</p>
                <div className="grid grid-cols-2 gap-2">
                  {item.factors.map((f) => (
                    <div key={f.label} className="text-sm">
                      <span className="text-muted-foreground">{f.label}: </span>
                      <span className="font-medium text-foreground">{f.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RiskCardComponent = ({ risk }: { risk: RiskCard }) => {
  const [expanded, setExpanded] = useState(false);
  const TrendIcon = trendIcons[risk.trend];

  return (
    <Card className={`border-l-4 ${severityStyles[risk.severity]}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
            <h3 className="font-semibold text-foreground">{risk.title}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {risk.timeHorizon === "immediate" ? "Immediate" : "Slow burn"}
            </Badge>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendIcon className="h-3.5 w-3.5" />
              <span className="capitalize">{risk.trend}</span>
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2">{risk.description}</p>

        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-8 mt-2"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
          {expanded ? "Less" : "Why this matters"}
        </Button>

        {expanded && (
          <div className="mt-3 border-t pt-3">
            <p className="text-sm text-foreground">{risk.whyItMatters}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AttentionRequired = () => {
  const { attentionItems, riskCards, isLoading, vehicleData } = useScaleMetrics();

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-8 max-w-3xl">
        {/* Attention Ranking */}
        <div className="space-y-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Attention Required</h1>
            <p className="text-muted-foreground mt-1">
              Where your attention is most valuable today — ranked by financial impact, risk, and recent changes.
            </p>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
            </div>
          ) : vehicleData.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Eye className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold text-foreground">Need fleet data</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Add vehicles and import trips and expenses to see attention rankings.
                </p>
              </CardContent>
            </Card>
          ) : attentionItems.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground">Fleet looks good</h3>
                <p className="text-sm text-muted-foreground mt-1">No vehicles need immediate attention right now.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {attentionItems.map((item) => (
                <AttentionCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>

        {/* Risk Indicators */}
        {!isLoading && vehicleData.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Risk Indicators</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Silent issues worth watching before they grow.
              </p>
            </div>

            {riskCards.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center">
                  <Shield className="h-8 w-8 text-primary mx-auto mb-2" />
                  <h3 className="font-semibold text-foreground text-sm">No risks flagged</h3>
                  <p className="text-xs text-muted-foreground mt-1">Fleet risk indicators are within normal ranges.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {riskCards.map((risk) => (
                  <RiskCardComponent key={risk.id} risk={risk} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default AttentionRequired;
